import React, { useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { API_URLS, getSelectedLanguage } from '../api/api';
import { languageManager } from '../utils/loadLanguage';

// Import components
import SponsorDropdown from './SponsorDropdown';
import LocationSelector from './LocationSelector';
import ImageUploader from './ImageUploader';

// A simple spinner component
const Spinner = () => (
  <div className="spinner-border text-primary" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const MemberForm = ({ memberData, mode, setIsFormVisible, onTableRefresh, onUserSelect }) => {
  // ----------------------------------------------------------------------------
  // Translation
  // ----------------------------------------------------------------------------
  const [translations, setTranslations] = useState({});
  const branchId = localStorage.getItem('branch_ID');

  useEffect(() => {
    async function loadLang() {
      const lang = getSelectedLanguage();
      await languageManager.loadLanguage(lang);
      setTranslations(languageManager.translations.memberForm || {});
    }
    loadLang();
  }, []);

  // ----------------------------------------------------------------------------
  // Utilities
  // ----------------------------------------------------------------------------
  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // ----------------------------------------------------------------------------
  // Initial form state
  // ----------------------------------------------------------------------------
  const getInitialFormState = () => ({
    first_name: memberData?.first_name || '',
    sur_name: memberData?.sur_name || '',
    email: memberData?.email || '',
    telephone: memberData?.telephone || '',
    birthday: memberData?.birthday ? getFormattedDate(memberData.birthday) : '',
    contact: memberData?.contact || '',
    image: memberData?.image || '',
    branch_ID: memberData?.branch_ID || 6,     // Default 6 (Online)
    position_ID: memberData?.position_ID || 1, // Default 1 (customer)
    password: generateRandomPassword()         // Auto-generate password if add
  });

  // ----------------------------------------------------------------------------
  // States
  // ----------------------------------------------------------------------------
  const [formData, setFormData] = useState(getInitialFormState());
  const [branches, setBranches] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(memberData?.image || '');
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '', loading: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  // ----------------------------------------------------------------------------
  // NEW: SPONSOR STATES
  // ----------------------------------------------------------------------------
  const [sponsors, setSponsors] = useState([]);
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  // ใช้ในโหมด add (ยังไม่มี user_ID) เพื่ออัปโหลดรูป
  const [tempUserId] = useState(`temp-${Date.now()}`);
  // pendingSponsorId => เมื่อเลือก sponsor จาก SponsorDropdown
  const [pendingSponsorId, setPendingSponsorId] = useState(null);

  // ----------------------------------------------------------------------------
  // LOCATION STATES & TOGGLE
  // ----------------------------------------------------------------------------
  const [enableLocation, setEnableLocation] = useState(false);
  const [jangwats, setJangwats] = useState([]);
  const [amphures, setAmphures] = useState([]);
  const [thamboons, setThamboons] = useState([]);
  const [selectedJangwat, setSelectedJangwat] = useState(null);
  const [selectedAmphure, setSelectedAmphure] = useState(null);
  const [selectedThamboon, setSelectedThamboon] = useState(null);

  // ----------------------------------------------------------------------------
  // useEffect - Branches & Positions
  // ----------------------------------------------------------------------------
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(API_URLS.BRANCHES_LIST, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setBranches(data);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    const fetchPositions = async () => {
      try {
        const response = await fetch(API_URLS.POSITION_LIST, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setPositions(data);
      } catch (error) {
        console.error('Error fetching positions:', error);
      }
    };

    fetchBranches();
    fetchPositions();
  }, []);

  // ----------------------------------------------------------------------------
  // If mode=edit => fetch user detail
  // ----------------------------------------------------------------------------
  useEffect(() => {
    const fetchUserData = async () => {
      if (mode === 'edit' && memberData?.user_ID) {
        try {
          const response = await fetch(API_URLS.SHOW_USER(memberData.user_ID), {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (!response.ok) throw new Error('Failed to fetch user data');
          const userData = await response.json();

          setFormData({
            first_name: userData.first_name || '',
            sur_name: userData.sur_name || '',
            email: userData.email || '',
            telephone: userData.telephone || '',
            birthday: userData.birthday ? getFormattedDate(userData.birthday) : '',
            contact: userData.contact || '',
            image: userData.image || '',
            branch_ID: userData.branch_ID || 6,
            position_ID: userData.position_ID || 1,
          });
          setPreviewImage(userData.image || '');

          // ถ้ามี sponsor
          if (userData.sponsor_id) {
            setSelectedSponsor(userData.sponsor_id);
          }

          // ถ้ามี location
          if (userData.jangwatID) {
            setSelectedJangwat(userData.jangwatID);
            setEnableLocation(true);
          }
          if (userData.amphoeID) {
            setSelectedAmphure(userData.amphoeID);
          }
          if (userData.thamboonID) {
            setSelectedThamboon(userData.thamboonID);
          }

        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        // โหมด add => reset form
        setFormData(getInitialFormState());
      }
    };

    fetchUserData();
  }, [memberData, mode]);

  // ----------------------------------------------------------------------------
  // Upload image (tempUserId ในโหมด add)
  // ----------------------------------------------------------------------------
  const uploadImage = async () => {
    const formDataImage = new FormData();
    const useUserId = (mode === 'add') ? tempUserId : memberData?.user_ID;

    formDataImage.append('uploadFile', selectedImage);
    formDataImage.append('user_ID', useUserId);
    setSubmitStatus(prev => ({ ...prev, loading: true }));

    try {
      const response = await fetch(API_URLS.IMAGE_UPLOAD, {
        method: 'POST',
        body: formDataImage,
      });
      if (!response.ok) throw new Error('Failed to upload image.');
      const imageResponse = await response.json();
      setSubmitStatus(prev => ({ ...prev, loading: false }));
      return imageResponse;
    } catch (error) {
      console.error('Image upload failed:', error);
      setSubmitStatus({ success: false, message: 'Failed to upload image.', loading: false });
      throw error;
    }
  };

  // ----------------------------------------------------------------------------
  // handleChange, handleBranchChange, handlePositionChange
  // ----------------------------------------------------------------------------
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBranchChange = (e) => {
    setFormData(prev => ({ ...prev, branch_ID: e.value }));
  };

  const handlePositionChange = (e) => {
    setFormData(prev => ({ ...prev, position_ID: e.value }));
  };

  // ----------------------------------------------------------------------------
  // handleSubmit
  // ----------------------------------------------------------------------------
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(prev => ({ ...prev, loading: true }));

    let updatedFormData = { ...formData };

    // ถ้ามีรูปใหม่ => upload ก่อน
    if (selectedImage) {
      try {
        const imageResp = await uploadImage();
        updatedFormData.image = imageResp.resizedFileURLs[0];
      } catch (error) {
        console.error('Image upload failed:', error);
        setSubmitStatus({ success: false, message: 'Failed to upload image.', loading: false });
        setIsSubmitting(false);
        return;
      }
    }

    // ถ้า edit => ไม่อัปเดต password
    if (mode === 'edit') {
      delete updatedFormData.password;
    }

    // ถ้าเปิด location => set location
    if (enableLocation) {
      updatedFormData.jangwatID = selectedJangwat || null;
      updatedFormData.amphoeID = selectedAmphure || null;
      updatedFormData.thamboonID = selectedThamboon || null;
    } else {
      updatedFormData.jangwatID = null;
      updatedFormData.amphoeID = null;
      updatedFormData.thamboonID = null;
    }

    // ใส่ date,time,ip
    const currentTime = new Date().toTimeString().split(' ')[0];
    const dataToSend = {
      ...updatedFormData,
      date: new Date().toISOString().split('T')[0],
      time: currentTime,
      ip: "192.168.1.15",
      user_ID: (mode === 'edit') ? memberData?.user_ID : undefined
    };

    // เลือก endpoint
    const apiURL = (mode === 'edit')
      ? API_URLS.EDIT_USER(memberData.user_ID)
      : API_URLS.INSERT_USER;

    const token = localStorage.getItem('token');

    try {
      console.log('Sending to user endpoint =>', dataToSend);
      const response = await fetch(apiURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorDetail = await response.json().catch(() => ({}));
        console.error('API responded with:', errorDetail);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // หลังจาก insert user
      const result = await response.json();
      console.log('User insert/update =>', result);

      // --------------------------------------------------------------
      // ถ้าเป็น add mode และมี pendingSponsorId => insert sponsor ต่อ
      // --------------------------------------------------------------
      if (mode === 'add' && pendingSponsorId) {
        if (!result.user_ID) {
          // server ไม่ส่ง user_ID
          console.error('Server did not return user_ID => Sponsor not inserted');
          setSubmitStatus({
            success: true,
            message: 'User added but user_ID not returned => sponsor not inserted',
            loading: false
          });
          setIsSubmitting(false);
          onTableRefresh();
          return;
        }

        // มี user_ID => เรียก INSERT_SPONSOR
        const sponsorData = [
          {
            user_id: result.user_ID,
            sponsor_id: pendingSponsorId,
            team_id: "A1",
            position: 1,
            commission_rate: 2.02
          }
        ];
        console.log('Sending sponsorData =>', sponsorData);

        const sponsorResp = await fetch(API_URLS.INSERT_SPONSOR(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(sponsorData)
        });

        if (!sponsorResp.ok) {
          const sponsorError = await sponsorResp.json().catch(() => ({}));
          console.error('Sponsor insert error:', sponsorError);
          throw new Error('Failed to insert sponsor');
        }
        const sponsorResult = await sponsorResp.json();
        console.log('Sponsor insert result =>', sponsorResult);
      }

      setSubmitStatus({ success: true, message: 'Successfully Recorded!', loading: false });
      setIsSubmitting(false);
      onTableRefresh();

    } catch (error) {
      console.error('Error during handleSubmit =>', error);
      setSubmitStatus({
        success: false,
        message: 'Error: ' + error.message,
        loading: false
      });
      setIsSubmitting(false);
    }
  };

  // ----------------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------------
  return (
    <div>
      <form onSubmit={handleSubmit}>

        {/* SponsorDropdown - logic for pendingSponsorId */}
        <SponsorDropdown
          sponsors={sponsors}
          setSponsors={setSponsors}
          selectedSponsor={selectedSponsor}
          setSelectedSponsor={setSelectedSponsor}
          translations={translations}
          branchId={branchId}
          memberData={memberData}
          mode={mode}
          pendingSponsorId={pendingSponsorId}
          setPendingSponsorId={setPendingSponsorId}
          tempUserId={tempUserId}
        />

        {/* Branch */}
        <div className="mb-3">
          <label htmlFor="branch" className="form-label">
            {translations.branchLabel || "Branch"}
          </label>
          <Dropdown
            value={formData.branch_ID}
            options={branches}
            onChange={handleBranchChange}
            optionLabel="branch_name"
            optionValue="branch_ID"
            placeholder={translations.branchPlaceholder || "Select a Branch"}
            className="w-100"
          />
        </div>

        {/* ImageUploader */}
        <ImageUploader
          mode={mode}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          previewImage={previewImage}
          setPreviewImage={setPreviewImage}
          translations={translations}
          memberData={memberData}
        />

        {/* Password (เฉพาะ add) */}
        {mode === 'add' && (
          <div className="mb-3 password-field">
            <label htmlFor="password" className="form-label">
              {translations.passwordLabel || "Password"}
            </label>
            <div className="input-group">
              <input
                type={passwordVisible ? "text" : "password"}
                className="form-control"
                id="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                <i className={passwordVisible ? "pi pi-eye-slash" : "pi pi-eye"}></i>
              </button>
            </div>
          </div>
        )}

        {/* Position (เฉพาะ add) */}
        {mode === 'add' && (
          <div className="mb-3">
            <label htmlFor="position" className="form-label">
              {translations.positionLabel || "Position"}
            </label>
            <Dropdown
              value={formData.position_ID}
              options={positions}
              onChange={handlePositionChange}
              optionLabel="position_Name"
              optionValue="position_ID"
              placeholder={translations.positionPlaceholder || "Select a Position"}
              className="w-100"
            />
          </div>
        )}

        {/* First Name */}
        <div className="mb-3">
          <label htmlFor="firstName" className="form-label">
            {translations.firstNameLabel || "First Name"}
          </label>
          <input
            type="text"
            className="form-control"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
          />
        </div>

        {/* Sur Name */}
        <div className="mb-3">
          <label htmlFor="sur_name" className="form-label">
            {translations.surnameLabel || "Surname"}
          </label>
          <input
            type="text"
            className="form-control"
            name="sur_name"
            value={formData.sur_name}
            onChange={handleChange}
          />
        </div>

        {/* Telephone */}
        <div className="mb-3">
          <label htmlFor="telephone" className="form-label">
            {translations.telephoneLabel || "Telephone"}
          </label>
          <input
            type="tel"
            className="form-control"
            name="telephone"
            value={formData.telephone}
            onChange={handleChange}
            required
          />
        </div>

        {/* Email */}
        <div className="mb-3">
          <label htmlFor="email" className="form-label">
            {translations.emailLabel || "Email"}
          </label>
          <input
            type="email"
            className="form-control"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        {/* Birthday */}
        <div className="mb-3">
          <label htmlFor="birthday" className="form-label">
            {translations.birthdayLabel || "Birthday"}
          </label>
          <input
            type="date"
            className="form-control"
            name="birthday"
            value={formData.birthday}
            onChange={handleChange}
            required
          />
        </div>

        {/* Address */}
        <div className="mb-3">
          <label htmlFor="contact" className="form-label">
            {translations.addressLabel || "Address"}
          </label>
          <input
            type="text"
            className="form-control"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
          />
        </div>

        {/* LocationSelector */}
        <LocationSelector
          enableLocation={enableLocation}
          setEnableLocation={setEnableLocation}
          jangwats={jangwats}
          setJangwats={setJangwats}
          amphures={amphures}
          setAmphures={setAmphures}
          thamboons={thamboons}
          setThamboons={setThamboons}
          selectedJangwat={selectedJangwat}
          setSelectedJangwat={setSelectedJangwat}
          selectedAmphure={selectedAmphure}
          setSelectedAmphure={setSelectedAmphure}
          selectedThamboon={selectedThamboon}
          setSelectedThamboon={setSelectedThamboon}
          translations={translations}
        />

        {/* Submit */}
        <div>
          <button type="submit" className="btn btn-success" disabled={isSubmitting}>
            {isSubmitting
              ? (translations.submitting || "Submitting...")
              : mode === 'edit'
                ? (translations.updateMember || "Update Member")
                : (translations.addMember || "Add Member")}
          </button>
          {isSubmitting && <Spinner />}
        </div>
      </form>

      {/* Status Message */}
      {submitStatus.message && (
        <div className={`alert ${submitStatus.success ? 'alert-success' : 'alert-danger'}`}>
          {submitStatus.message}
        </div>
      )}
    </div>
  );
};

export default MemberForm;
